import { View } from "react-native";
import React, { useState, useEffect, useCallback } from "react";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedKebabMenu, IKebabActionItem } from "@/components/ThemedKebabMenu";

import { useFirebase } from "@components/firebase/useFirebase";
import { getPlace, editPlace, deletePlace } from "@/services/firebase/place-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { PlaceModel, ShareModel } from "common247";
import { PlaceEditor } from "@/components/places/PlaceEditor";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { navigateBackOrToList } from "@/utils/navigation";
import { entitySync } from "@/services/sync/entity-sync";

/**
 * Screen for viewing and editing a Place entity.
 */
export default function EditPlaceScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth(true);
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [place, setPlace] = useState<PlaceModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  const fetchData = useCallback(async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const res = await getPlace(services, { placeId: id });
      if (res.status && res.place) {
        setPlace(res.place);
        if (res.place.canShare) {
          fetchShares();
        }
      } else {
        CustomAlert.alert("Error", res.error || "Place not found.");
        navigateBackOrToList(router, "/(tabs)/my-places", navigation);
      }
    } catch (error: any) {
      console.error("[EditPlaceScreen] Fetch error:", error);
      CustomAlert.alert("Error", error.message || "Failed to load place.");
    } finally {
      setLoading(false);
    }
  }, [services, id]);

  const fetchShares = async () => {
    if (!services || !id) return;
    setIsLoadingShares(true);
    try {
      const res = await getSharesForSource(services, { sourceId: id, sourceType: "place" });
      if (res.status && res.shares) {
        setShares(res.shares);
      }
    } catch (error: any) {
      console.error("[EditPlaceScreen] Error fetching shares:", error);
    } finally {
      setIsLoadingShares(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time broadcast entity sync
  useEffect(() => {
    const unsub = entitySync.subscribe((event) => {
      if (event.type === "place" && event.id === id) {
        fetchData();
      }
    });
    return () => unsub();
  }, [id, fetchData]);

  const handleSave = async (formData: any) => {
    if (!services || !place || !id) return;

    setIsSaving(true);
    try {
      const res = await editPlace(services, {
        placeId: id,
        ...formData,
      });

      if (res.status) {
        const updatedPlace: PlaceModel = {
          ...(place || ({} as PlaceModel)),
          ...formData,
          id,
        };
        // Auto-save updates in place — do not close the edited item
        setPlace((prev) => (prev ? { ...prev, ...formData } : updatedPlace));
        entitySync.emit({
          type: "place",
          action: "update",
          id,
          data: updatedPlace,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to save place.");
      }
    } catch (error: any) {
      console.error("[EditPlaceScreen] Save error:", error);
      CustomAlert.alert("Error", error.message || "Unexpected error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!services || !id) return;

    await confirmEntityDeletion({
      services,
      entityType: "place",
      entityId: id,
      entityName: place?.name,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deletePlace(services, { placeId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-places", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const handleShareTarget = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services || !id) return;
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "place",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchShares();
      } else {
        CustomAlert.alert("Sharing Error", res.error || "Failed to share place.");
      }
    } catch (err: any) {
      CustomAlert.alert("Sharing Error", err.message || "Failed to share place.");
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchShares();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to revoke share.");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "Failed to revoke share.");
    }
  };

  const userRole = place?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const kebabActions: IKebabActionItem[] = [];
  if (place && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Place",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={place ? `${isViewer ? "View" : "Edit"}: ${place.name}` : "Place Details"}
      loading={loading}
      rightComponent={kebabActions.length > 0 ? <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Place options" /> : undefined}
    >
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && place && (
          <PlaceEditor
            place={place}
            shares={shares}
            isLoadingShares={isLoadingShares}
            currentUserId={user?.uid}
            onSave={handleSave}
            onShare={handleShareTarget}
            onUnshare={handleUnshare}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-places", navigation)}
            isSaving={isSaving}
          />
        )}
      </View>
    </ThemedScreen>
  );
}
